import {MaterialType, MaterialWithRecipes} from '@domain/materials'
import type {Asset} from '@hconnect/common/types'
import {dataTestId} from '@hconnect/uikit'
import {InfoOutlined} from '@mui/icons-material'
import {Checkbox, FormControlLabel} from '@mui/material'
import {useIsMutating} from '@tanstack/react-query'
import React, {useEffect, useState} from 'react'
import {useFormContext} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {ControlledAutocomplete, ControlledTextField} from '../../../../common/components'
import {maxLengthValidator, requiredValidator} from '../../../../common/utils'
import {MaterialFormDefaultValues} from '../../materials-details/ExistingMaterialDetails/hooks'
import {MaterialsGrid} from '../components/MaterialsGrid'
import {MaterialsGridItem} from '../components/MaterialsGridItem'

import {MaterialSourceInputs} from './MaterialSourceInputs'

import {Schedule} from '@settings/modules/common/types'
import {MAX_MATERIAL_NAME_LENGTH} from '@settings/modules/materials/consts'
import {useUpdateMaterialStatus} from '@settings/modules/materials/hooks/mutations/useUpdateMaterialStatus'
import {useUrlParam} from '@settings/modules/routing/hooks/useUrlParam'

interface MaterialFormProps {
  material: MaterialWithRecipes | undefined
  isReadOnly: boolean
  schedule: Schedule
  assets: Asset[]
  globalMaterialOptions: {id: string; label: string}[]
}

export const MaterialForm: React.FC<MaterialFormProps> = ({
  material,
  isReadOnly,
  schedule,
  assets,
  globalMaterialOptions
}) => {
  const {t} = useTranslation()

  const {control} = useFormContext<MaterialFormDefaultValues>()
  const runningMutations = useIsMutating({mutationKey: ['editMaterial']})
  const isMaterialSaving = Boolean(runningMutations)

  const isNewMaterial = material === undefined
  const [active, setActive] = useState(false)
  const {mutate: toggleStatus} = useUpdateMaterialStatus()
  const plantCode = useUrlParam('plantCode')

  useEffect(() => {
    if (material) {
      setActive(Boolean(material.isActive))
    }
  }, [material])

  const handleToggle = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (!material) return
    const prev = Boolean(material.isActive)
    setActive(checked)

    toggleStatus(
      {plantCode: plantCode, materialId: material.id, isActive: checked},
      {
        onError: () => {
          setActive(prev)
        }
      }
    )
  }

  return (
    <MaterialsGrid {...dataTestId('add_edit_material_form')}>
      {!isNewMaterial && (
        <MaterialsGridItem>
          <FormControlLabel
            control={
              <Checkbox
                {...dataTestId('material_active_checkbox')}
                disabled={isReadOnly || isMaterialSaving}
                checked={active}
                onChange={handleToggle}
              />
            }
            label={t('materialsSettings.active')}
          />
        </MaterialsGridItem>
      )}

      <MaterialsGridItem>
        <ControlledAutocomplete
          control={control}
          formDataName="type"
          options={Object.values(MaterialType)}
          translateOption={(option) => t(`materialsSettings.materialTypes.${option}`)}
          label={t('materialsSettings.type')}
          rules={requiredValidator(t)}
          disabled={isReadOnly || !isNewMaterial || isMaterialSaving}
          {...dataTestId('material_type_input')}
        />
      </MaterialsGridItem>
      {isNewMaterial && (
        <MaterialsGridItem
          direction="row"
          sx={{
            fontSize: '12px',
            lineHeight: '16px',
            color: (theme) => theme.palette.text.secondary
          }}
          {...dataTestId('material_form_tooltip')}
        >
          <InfoOutlined sx={{mr: 1, height: 16, width: 16}} />
          {t('materialsSettings.typeTooltip')}
        </MaterialsGridItem>
      )}
      <MaterialsGridItem>
        <ControlledTextField
          control={control}
          formDataName="name"
          rules={{...requiredValidator(t), ...maxLengthValidator(t, MAX_MATERIAL_NAME_LENGTH)}}
          label={t('materialsSettings.localName')}
          TextFieldProps={{
            disabled: isReadOnly || isMaterialSaving,
            inputProps: {maxLength: MAX_MATERIAL_NAME_LENGTH},
            sx: ({spacing, palette}) => ({
              '& .MuiInputBase-root': {
                height: spacing(7),
                borderRadius: spacing(1),
                boxShadow: 'none',
                background: palette.background.paper
              }
            })
          }}
          {...dataTestId('material_name_input')}
        />
      </MaterialsGridItem>
      <MaterialsGridItem>
        <ControlledTextField
          control={control}
          formDataName="description"
          label={t('materialsSettings.descritpion')}
          TextFieldProps={{
            disabled: isReadOnly || isMaterialSaving,
            sx: ({spacing, palette}) => ({
              '& .MuiInputBase-root': {
                height: spacing(7),
                borderRadius: spacing(1),
                boxShadow: 'none',
                background: palette.background.paper
              }
            })
          }}
          {...dataTestId('material_description_input')}
        />
      </MaterialsGridItem>
      {!isNewMaterial && (
        <MaterialsGridItem>
          <ControlledAutocomplete
            control={control}
            formDataName="globalMaterial"
            options={globalMaterialOptions}
            label={t('materialsSettings.globalMaterial')}
            disableClearable={false}
            disabled={isReadOnly || isMaterialSaving}
            {...dataTestId('material_global_material_input')}
          />
        </MaterialsGridItem>
      )}
      <MaterialSourceInputs
        material={material}
        schedule={schedule}
        assets={assets}
        disabled={isReadOnly || isMaterialSaving}
      />
    </MaterialsGrid>
  )
}
